import React, { useCallback, useEffect } from "react";
import styles from "./FixedPositioningAroundElement.module.scss";
import classnames from "classnames";
import { TurnrPortal } from "../portal/TurnrPortal";
import useGetElementSizeState from "rylak-common/src/hooks/useGetElementSizeState.jsx";
import useTurnrCallbackRef from "rylak-common/src/hooks/useTurnrCallbackRef.jsx";

const TOOLTIP_DISTANCE_TO_EDGE = 10;
const TOOLTIP_DISTANCE_TO_ANCHOR = 5;

export default function useFixedPositioningAroundElement(props) {
    const { placement = "top", elementRef, fixedElementRef, minWidthAsElement } = props;
    const [windowSize] = useGetElementSizeState({
        domElement: window?.document?.body,
    });

    const setPositioning = useCallback(() => {
        const elementPosData = elementRef?.current?.getBoundingClientRect();
        const fixedElementPosData = fixedElementRef?.current?.getBoundingClientRect();

        if (fixedElementRef?.current) {
            const { style } = fixedElementRef.current;
            const maxHeight = window.innerHeight - TOOLTIP_DISTANCE_TO_EDGE * 2;
            const maxWidth = window.innerWidth - TOOLTIP_DISTANCE_TO_EDGE * 2;
            style.maxHeight = `${maxHeight}px`;
            style.maxWidth = `${maxWidth}px`;
            let fixedElementWidth = fixedElementPosData?.width;
            if (minWidthAsElement) {
                const minWidth = elementPosData?.width;
                style.minWidth = `${minWidth}px`;
                fixedElementWidth = Math.max(minWidth, fixedElementWidth);
            }

            if (placement.startsWith("bottom") || placement.startsWith("top")) {
                if (placement === "top-left" || placement === "bottom-left") {
                    const left = Math.min(elementPosData?.x, window.innerWidth - fixedElementWidth - TOOLTIP_DISTANCE_TO_EDGE);
                    style.left = `${left}px`;
                    style.right = "-2000px";
                    style.maxWidth = `${window.innerWidth - elementPosData?.x - TOOLTIP_DISTANCE_TO_EDGE}px`;
                } else if (placement === "top-right" || placement === "bottom-right") {
                    const right = window.innerWidth - (elementPosData?.x + elementPosData?.width);
                    style.left = "initial";
                    style.right = `${right}px`;
                    style.maxWidth = `${window.innerWidth - right - TOOLTIP_DISTANCE_TO_EDGE}px`;
                } else {
                    const left = Math.max(TOOLTIP_DISTANCE_TO_EDGE, elementPosData?.x + (elementPosData?.width - fixedElementWidth) / 2);
                    if (left + fixedElementWidth + TOOLTIP_DISTANCE_TO_EDGE > window.innerWidth) {
                        // we try right instead
                        style.right = `${TOOLTIP_DISTANCE_TO_EDGE}px`;
                        style.left = "initial";
                    } else {
                        style.left = `${left}px`;
                        style.right = "initial";
                    }
                }

                if (placement.startsWith("top")) {
                    const top = Math.max(TOOLTIP_DISTANCE_TO_EDGE, elementPosData?.y - TOOLTIP_DISTANCE_TO_ANCHOR - fixedElementPosData.height);
                    style.top = `${top}px`;
                    style.bottom = "initial";
                }

                if (placement.startsWith("bottom")) {
                    const top = Math.max(TOOLTIP_DISTANCE_TO_EDGE, elementPosData?.y + elementPosData?.height + TOOLTIP_DISTANCE_TO_ANCHOR);

                    if (top + fixedElementPosData.height + TOOLTIP_DISTANCE_TO_EDGE > window.innerHeight) {
                        // we try from bottom instead
                        style.top = "initial";
                        style.bottom = `${TOOLTIP_DISTANCE_TO_EDGE}px`;
                    } else {
                        style.top = `${top}px`;
                        style.bottom = "initial";
                    }
                }
            } else if (placement === "left" || placement === "right") {
                const top = Math.max(TOOLTIP_DISTANCE_TO_EDGE, elementPosData?.y + elementPosData?.height / 2 - fixedElementPosData?.height / 2);
                style.top = `${top}px`;
                style.bottom = "initial";

                if (placement === "left") {
                    const left = Math.max(TOOLTIP_DISTANCE_TO_EDGE, elementPosData?.x - (fixedElementWidth + TOOLTIP_DISTANCE_TO_ANCHOR));
                    style.left = `${left}px`;
                    style.right = "-2000px";
                }

                if (placement === "right") {
                    const right = Math.max(TOOLTIP_DISTANCE_TO_EDGE, window.innerWidth - elementPosData?.x - elementPosData?.width - TOOLTIP_DISTANCE_TO_ANCHOR - fixedElementWidth);
                    style.left = "initial";
                    style.right = `${right}px`;
                }
            }
        }
    }, [fixedElementRef, elementRef, placement]);
    useEffect(() => {
        setPositioning();
    }, [elementRef, fixedElementRef, placement, windowSize]);

    return setPositioning;
}

export function FixedDropdown({ open, setOpen, placement = "bottom-left", elementRef, dropdownRef = useTurnrCallbackRef(), children }) {
    const setFixedPositioning = useFixedPositioningAroundElement({
        placement: placement,
        elementRef: elementRef,
        fixedElementRef: dropdownRef,
        minWidthAsElement: true,
    });

    useEffect(() => {
        setFixedPositioning();
    }, [open, setFixedPositioning]);

    return (
        <TurnrPortal open={open}>
            <div ref={dropdownRef} className={classnames(styles["dropdown"], !open && styles["dropdown-hide"])} onClick={(e) => e.stopPropagation()}>
                {children}
            </div>
            {open && (
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                    }}
                    className={styles["backdrop"]}
                />
            )}
        </TurnrPortal>
    );
}
